import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Box, Paper, FormControl, InputLabel, Select, MenuItem, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { AuthService } from '../../services/auth.service';
import { UserType } from '../../interfaces/userType.enum';
import { useToast } from '../../context/ToastContext';
import { validatePassword } from '../../utils/passwordPolicy';

const UserCreate = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [user, setUser] = useState({ usernameSuffix: '', password: '', phone: '', type: '' });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [institutionCode, setInstitutionCode] = useState<string | null>(null);
    const [isLoadingCode, setIsLoadingCode] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const effectiveUsername = institutionCode
        ? `${institutionCode}_${user.usernameSuffix}`
        : user.usernameSuffix;
    const passwordErrors = validatePassword(user.password, effectiveUsername, user.phone);

    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) {
            setIsLoadingCode(false);
            return;
        }
        InstitutionService.getInstitutionById(institutionId)
            .then((data: { code: string }) => {
                setInstitutionCode(data.code);
                setIsLoadingCode(false);
            })
            .catch(() => {
                toast.showError('Failed to load institution details');
                setIsLoadingCode(false);
            });
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        if (!user.usernameSuffix || !user.password || !confirmPassword || !user.phone || !user.type) {
            toast.showError('All fields are required');
            setIsLoading(false);
            return;
        }

        if (user.password !== confirmPassword) {
            toast.showError('Password and confirm password do not match');
            setIsLoading(false);
            return;
        }

        if (passwordErrors.length > 0) {
            toast.showError('Password does not meet the strength requirements');
            setIsLoading(false);
            return;
        }

        try {
            await UserService.createUser({
                username: effectiveUsername,
                password: user.password,
                phone: user.phone,
                type: user.type as UserType
            });
            navigate('/users');
        } catch (err) {
            toast.showApiError(err, 'Failed to create user');
            console.error('Error creating user:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <Box sx={{ maxWidth: 600, margin: '0 auto' }}>
                <Paper sx={{ p: 3 }}>
                    <h2>Create New User</h2>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Username"
                            value={user.usernameSuffix}
                            onChange={(e) => setUser({ ...user, usernameSuffix: e.target.value.replace(/\s/g, '') })}
                            margin="normal"
                            required
                            InputProps={institutionCode ? {
                                startAdornment: (
                                    <InputAdornment position="start">{institutionCode}_</InputAdornment>
                                )
                            } : {}}
                        />
                        <TextField
                            fullWidth
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={user.password}
                            onChange={(e) => setUser({ ...user, password: e.target.value })}
                            margin="normal"
                            required
                            error={user.password.length > 0 && passwordErrors.length > 0}
                            helperText={user.password.length > 0 && passwordErrors.length > 0 ? passwordErrors.join('; ') : ''}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            aria-label={showPassword ? 'hide password' : 'show password'}
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Confirm Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            margin="normal"
                            required
                            error={confirmPassword.length > 0 && confirmPassword !== user.password}
                            helperText={confirmPassword.length > 0 && confirmPassword !== user.password ? 'Passwords do not match' : ''}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                            size="small"
                                            aria-label={showConfirmPassword ? 'hide confirm password' : 'show confirm password'}
                                        >
                                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Phone"
                            value={user.phone}
                            onChange={(e) => setUser({ ...user, phone: e.target.value })}
                            margin="normal"
                            required
                        />
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>User Type</InputLabel>
                            <Select
                                value={user.type}
                                onChange={(e) => setUser({ ...user, type: e.target.value })}
                                label="User Type"
                            >
                                {Object.values(UserType)
                                    .filter(type => type !== UserType.SystemAdmin)
                                    .map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                type="button"
                                onClick={() => navigate('/users')}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isLoading || institutionCode === null || !user.usernameSuffix || !user.password || !confirmPassword || user.password !== confirmPassword || !user.phone || !user.type || passwordErrors.length > 0}
                            >
                                {isLoading ? <CircularProgress size={24} /> : 'Create User'}
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Box>
        </div>
    );
};

export default UserCreate;
